import type { FunctionReturnType } from "convex/server";
import type { Id } from "../../../convex/_generated/dataModel";
import type { api } from "../../../convex/_generated/api";
import { isInsufficientCreditError } from "../../lib/app-errors";
import type { LoggingFlowPayload } from "../../lib/logging-flow";
import { parseOptionalMetric } from "../meal-review/review-utils";

// Nutrition-label scan flow pure logic (plan todo 18a). The component
// (LabelScanFlow.tsx) is a thin shell over this reducer + these controllers so
// the repo's node-env test pattern (renderToStaticMarkup + pure functions, NO
// jsdom) can assert every step transition, the custom-food payload, and the
// credit/failure error split without a DOM.
//
// Flow: capture/upload label photo -> api.analyze.analyzeNutritionLabel ->
// correction form (every field editable, BLANK ALLOWED = unknown) ->
// "Save as custom food" (api.customFoods.create source "label") -> offer
// "Log a serving" (startFlow review with dataSource "nutrition-label").
// Nothing here ever zero-fills a blank field.

export type LabelScanAnalysis = FunctionReturnType<typeof api.analyze.analyzeNutritionLabel>;

export type LabelFlowPhase =
  | "pick" // choose capture / library
  | "uploading"
  | "upload-error"
  | "confirm" // uploaded; user confirms the photo before spending a credit
  | "analyzing"
  | "analysis-error" // ANALYSIS_FAILED -> Retry / Enter manually
  | "correct" // correction form
  | "saved"; // custom food saved; offer "Log a serving"

export interface LabelFormFields {
  readonly name: string;
  readonly brand: string;
  readonly servingUnit: string;
  readonly servingWeightG: string;
  readonly calories: string;
  readonly proteinG: string;
  readonly carbsG: string;
  readonly fatG: string;
  readonly fiberG: string;
}

export const EMPTY_LABEL_FORM: LabelFormFields = {
  name: "",
  brand: "",
  servingUnit: "",
  servingWeightG: "",
  calories: "",
  proteinG: "",
  carbsG: "",
  fatG: "",
  fiberG: "",
};

export interface LabelFlowState {
  readonly phase: LabelFlowPhase;
  // Retained across upload attempts so Retry needs no second picker round.
  readonly file: File | null;
  readonly fileName: string | null;
  readonly previewUrl: string | null;
  readonly storageId: string | null;
  readonly form: LabelFormFields;
  // Extra nutrients the label reported beyond the core nine fields; saved
  // verbatim with the custom food (additionalNutrientsJson), never parsed
  // into core fields.
  readonly additionalNutrientsJson: string | null;
  readonly errorMessage: string | null;
  // Inline save error — the form values stay exactly as the user left them.
  readonly saveError: string | null;
  readonly saving: boolean;
  readonly creditDrawerOpen: boolean;
}

export const INITIAL_LABEL_FLOW_STATE: LabelFlowState = {
  phase: "pick",
  file: null,
  fileName: null,
  previewUrl: null,
  storageId: null,
  form: EMPTY_LABEL_FORM,
  additionalNutrientsJson: null,
  errorMessage: null,
  saveError: null,
  saving: false,
  creditDrawerOpen: false,
};

export type LabelFlowAction =
  | { readonly type: "filePicked"; readonly file: File }
  | { readonly type: "fileRejected"; readonly message: string }
  | { readonly type: "uploadSucceeded"; readonly storageId: string; readonly previewUrl: string }
  | { readonly type: "uploadFailed"; readonly message: string }
  | { readonly type: "retryUpload" }
  | { readonly type: "analyzeStarted" }
  | { readonly type: "analyzeSucceeded"; readonly analysis: LabelScanAnalysis }
  | { readonly type: "analyzeFailed"; readonly message: string; readonly credits: boolean }
  | { readonly type: "fieldChanged"; readonly field: keyof LabelFormFields; readonly value: string }
  | { readonly type: "enterManually" }
  | { readonly type: "saveStarted" }
  | { readonly type: "saveFailed"; readonly message: string }
  | { readonly type: "saveSucceeded" }
  | { readonly type: "creditDrawerClosed" }
  | { readonly type: "pickDifferentPhoto" };

function numberToField(value: number | null): string {
  return value === null ? "" : String(value);
}

// Validator nulls become blank inputs — unknown stays unknown, never 0.
export function formFromScan(scan: LabelScanAnalysis): LabelFormFields {
  return {
    name: scan.name ?? "",
    brand: scan.brand ?? "",
    servingUnit: scan.servingUnit ?? "",
    servingWeightG: numberToField(scan.servingWeightG),
    calories: numberToField(scan.calories),
    proteinG: numberToField(scan.proteinG),
    carbsG: numberToField(scan.carbsG),
    fatG: numberToField(scan.fatG),
    fiberG: numberToField(scan.fiberG),
  };
}

export function labelFlowReducer(state: LabelFlowState, action: LabelFlowAction): LabelFlowState {
  switch (action.type) {
    case "filePicked":
      return {
        ...state,
        phase: "uploading",
        file: action.file,
        fileName: action.file.name,
        errorMessage: null,
      };
    case "fileRejected":
      return { ...state, phase: "pick", file: null, errorMessage: action.message };
    case "uploadSucceeded":
      if (state.phase !== "uploading") return state;
      return {
        ...state,
        phase: "confirm",
        storageId: action.storageId,
        previewUrl: action.previewUrl,
        errorMessage: null,
      };
    case "uploadFailed":
      if (state.phase !== "uploading") return state;
      return { ...state, phase: "upload-error", errorMessage: action.message };
    case "retryUpload":
      if (state.phase !== "upload-error" || state.file === null) return state;
      return { ...state, phase: "uploading", errorMessage: null };
    case "analyzeStarted":
      // From the confirm step OR directly from a failed analysis (Retry).
      if (
        (state.phase !== "confirm" && state.phase !== "analysis-error") ||
        state.storageId === null
      ) {
        return state;
      }
      return { ...state, phase: "analyzing", errorMessage: null };
    case "analyzeSucceeded":
      if (state.phase !== "analyzing") return state;
      return {
        ...state,
        phase: "correct",
        form: formFromScan(action.analysis),
        additionalNutrientsJson:
          action.analysis.additional.length > 0
            ? JSON.stringify(action.analysis.additional)
            : null,
        errorMessage: null,
      };
    case "analyzeFailed":
      if (state.phase !== "analyzing") return state;
      if (action.credits) {
        // Credits are recoverable in place: back to the confirm step with the
        // drawer open; the uploaded photo is still there.
        return { ...state, phase: "confirm", creditDrawerOpen: true, errorMessage: null };
      }
      return { ...state, phase: "analysis-error", errorMessage: action.message };
    case "fieldChanged":
      return {
        ...state,
        form: { ...state.form, [action.field]: action.value },
        saveError: null,
      };
    case "enterManually":
      // Blank/preserved correction form — the user types the label values
      // themselves. Never reachable before upload, so storageId may exist.
      return { ...state, phase: "correct", errorMessage: null, saveError: null };
    case "saveStarted":
      if (state.phase !== "correct") return state;
      return { ...state, saving: true, saveError: null };
    case "saveFailed":
      if (state.phase !== "correct") return state;
      // Form state is intentionally untouched (preserved) on failure.
      return { ...state, saving: false, saveError: action.message };
    case "saveSucceeded":
      if (state.phase !== "correct") return state;
      return { ...state, phase: "saved", saving: false, saveError: null };
    case "creditDrawerClosed":
      return { ...state, creditDrawerOpen: false };
    case "pickDifferentPhoto":
      return {
        ...INITIAL_LABEL_FLOW_STATE,
        creditDrawerOpen: state.creditDrawerOpen,
      };
  }
}

// ---------------------------------------------------------------------------
// Payload builders — blank fields are OMITTED, never zero-filled.
// ---------------------------------------------------------------------------

const NUMERIC_FIELD_LABELS = {
  servingWeightG: "Serving weight",
  calories: "Calories",
  proteinG: "Protein",
  carbsG: "Carbs",
  fatG: "Fat",
  fiberG: "Fibre",
} as const;

type NumericField = keyof typeof NUMERIC_FIELD_LABELS;

export interface CustomFoodCreateArgs {
  readonly name: string;
  readonly brand?: string;
  readonly servingUnit: string;
  readonly servingWeightG?: number;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly additionalNutrientsJson?: string;
  readonly source: "label";
}

export type CustomFoodArgsResult =
  | { readonly ok: true; readonly args: CustomFoodCreateArgs }
  | { readonly ok: false; readonly error: string };

// api.customFoods.create requires name + servingUnit; every nutrition field
// is optional and a blank input means UNKNOWN (omitted, never 0). A non-blank
// non-numeric value is an inline validation error, not silent data loss.
export function buildCustomFoodArgs(
  form: LabelFormFields,
  additionalNutrientsJson: string | null,
): CustomFoodArgsResult {
  const name = form.name.trim();
  if (name.length === 0) return { ok: false, error: "Add a food name first." };
  const servingUnit = form.servingUnit.trim();
  if (servingUnit.length === 0) {
    return { ok: false, error: "Add a serving unit (e.g. serving, bar, g)." };
  }
  const parsed: Partial<Record<NumericField, number>> = {};
  for (const field of Object.keys(NUMERIC_FIELD_LABELS) as NumericField[]) {
    const raw = form[field];
    if (raw.trim().length === 0) continue;
    const value = parseOptionalMetric(raw);
    if (value === undefined) {
      return { ok: false, error: `${NUMERIC_FIELD_LABELS[field]} must be a non-negative number.` };
    }
    parsed[field] = value;
  }
  const brand = form.brand.trim();
  return {
    ok: true,
    args: {
      name,
      ...(brand.length > 0 ? { brand } : {}),
      servingUnit,
      ...(parsed.servingWeightG === undefined ? {} : { servingWeightG: parsed.servingWeightG }),
      ...(parsed.calories === undefined ? {} : { calories: parsed.calories }),
      ...(parsed.proteinG === undefined ? {} : { proteinG: parsed.proteinG }),
      ...(parsed.carbsG === undefined ? {} : { carbsG: parsed.carbsG }),
      ...(parsed.fatG === undefined ? {} : { fatG: parsed.fatG }),
      ...(parsed.fiberG === undefined ? {} : { fiberG: parsed.fiberG }),
      ...(additionalNutrientsJson === null ? {} : { additionalNutrientsJson }),
      source: "label",
    },
  };
}

// "Log a serving" after saving: a single review item carrying the corrected
// values (one serving of the saved unit) with dataSource "nutrition-label".
// Blank fields stay ABSENT so the review keeps them unknown.
export function buildLabelReviewPayload(form: LabelFormFields): LoggingFlowPayload {
  const calories = parseOptionalMetric(form.calories);
  const proteinG = parseOptionalMetric(form.proteinG);
  const carbsG = parseOptionalMetric(form.carbsG);
  const fatG = parseOptionalMetric(form.fatG);
  const fiberG = parseOptionalMetric(form.fiberG);
  return {
    items: [
      {
        name: form.name.trim(),
        quantity: 1,
        unit: form.servingUnit.trim(),
        ...(calories === undefined ? {} : { calories }),
        ...(proteinG === undefined ? {} : { proteinG }),
        ...(carbsG === undefined ? {} : { carbsG }),
        ...(fatG === undefined ? {} : { fatG }),
        ...(fiberG === undefined ? {} : { fiberG }),
        dataSource: "nutrition-label",
        confidence: "high",
      },
    ],
    source: "label",
  };
}

// ---------------------------------------------------------------------------
// Controllers (todo-21 pattern): the component only dispatches outcomes.
// ---------------------------------------------------------------------------

export type AnalyzeNutritionLabelFn = (args: {
  storageId: Id<"_storage">;
}) => Promise<LabelScanAnalysis>;

export type LabelAnalysisOutcome =
  | { readonly ok: true; readonly analysis: LabelScanAnalysis }
  | { readonly ok: false; readonly credits: boolean; readonly message: string };

// INSUFFICIENT_CREDITS -> CreditDrawer; everything else (ANALYSIS_FAILED) ->
// Retry / Enter manually.
export async function runNutritionLabelAnalysis(
  analyze: AnalyzeNutritionLabelFn,
  storageId: Id<"_storage">,
): Promise<LabelAnalysisOutcome> {
  try {
    const analysis = await analyze({ storageId });
    console.log("[Frontend][LabelScanFlow] Label analysis completed", {
      hasName: analysis.name !== null,
      hasCalories: analysis.calories !== null,
      additionalCount: analysis.additional.length,
    });
    return { ok: true, analysis };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    const credits = isInsufficientCreditError(error);
    console.error("[Frontend][LabelScanFlow] Label analysis failed", { credits, error: message });
    return { ok: false, credits, message };
  }
}

export type CreateCustomFoodFn = (args: CustomFoodCreateArgs) => Promise<unknown>;

export type SaveCustomFoodOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

// Create rejection -> inline error; the caller keeps the correction form
// state exactly as the user left it.
export async function runSaveCustomFood(
  create: CreateCustomFoodFn,
  args: CustomFoodCreateArgs,
): Promise<SaveCustomFoodOutcome> {
  try {
    await create(args);
    console.log("[Frontend][LabelScanFlow] Custom food saved from label", {
      name: args.name,
      hasCalories: args.calories !== undefined,
    });
    return { ok: true };
  } catch (error: unknown) {
    console.error("[Frontend][LabelScanFlow] Custom food save failed", {
      name: args.name,
      error: error instanceof Error ? error.message : String(error),
    });
    return {
      ok: false,
      error: "Could not save this custom food. Your entries are unchanged — try again.",
    };
  }
}
